import { LogIn, Loader2, Search, ShoppingCart, X, Wallet, Gift } from 'lucide-react';
import { useRef, useEffect, useState } from 'react';
import { UserMenu } from '../UserMenu';
import { ThemeColors, isGiftClaimClaimed } from '../../types';
import { useUser } from '../../contexts/UserContext';
import { useGuestMode } from '../../hooks/useGuestMode';

interface HeaderActionsProps {
  isSearchOpen: boolean;
  onSearchOpen: () => void;
  onMobileSearchOpen: () => void;
  onSearchClose: () => void;
  searchQuery: string;
  onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  cartItemsCount: number;
  onCartClick: () => void;
  onCartMouseEnter: () => void;
  onCartMouseLeave: () => void;
  onShowPromos: () => void;
  onShowGiftClaims: () => void;
  isMenuOpen: boolean;
  onToggleMenu: () => void;
  themeColors?: ThemeColors;
  showCart?: boolean;
  searchLoading?: boolean;
  productsLoading?: boolean;
}

export function HeaderActions({
  isSearchOpen,
  onSearchOpen,
  onMobileSearchOpen,
  onSearchClose,
  searchQuery,
  onSearchChange,
  cartItemsCount,
  onCartClick,
  onCartMouseEnter,
  onCartMouseLeave,
  onShowPromos,
  onShowGiftClaims,
  themeColors,
  showCart = true,
  searchLoading = false,
  productsLoading = false,
}: HeaderActionsProps) {
  const { promoCodes, giftClaims } = useUser();
  const { isGuest, isAuthenticated } = useGuestMode();
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [isClosingSearch, setIsClosingSearch] = useState(false);
  const [closingSpinner, setClosingSpinner] = useState(false);
  const closingLoadingStartedRef = useRef(false);
  const isUserClosingRef = useRef(false);

  const handleClose = () => {
    setIsClosingSearch(true);
    setTimeout(() => {
      onSearchClose();
      setIsClosingSearch(false);
    }, 200);
  };

  useEffect(() => {
    if (isSearchOpen) {
      setIsClosingSearch(false);
      setClosingSpinner(false);
      closingLoadingStartedRef.current = false;
      isUserClosingRef.current = false;
      searchInputRef.current?.focus();
    }
  }, [isSearchOpen]);

  useEffect(() => {
    if (!closingSpinner) return;
    if (productsLoading) {
      closingLoadingStartedRef.current = true;
    } else if (closingLoadingStartedRef.current) {
      closingLoadingStartedRef.current = false;
      setClosingSpinner(false);
    }
  }, [productsLoading, closingSpinner]);

  const handleClearAndClose = () => {
    if (searchQuery.trim()) {
      onSearchChange({ target: { value: '' } } as React.ChangeEvent<HTMLInputElement>);
      isUserClosingRef.current = true;
      handleClose();
      setClosingSpinner(true);
    } else {
      handleClose();
    }
  };

  const navTextColor = themeColors?.left_side_color || '#e5e7eb';
  const cartBadgeBackground = themeColors?.header_cart_background || '#6D6A69';
  const cartBadgeText = themeColors?.header_cart_text_color || 'white';

  let promoCodeBalance: number | null = null;
  let promoCount = 0;
  if (promoCodes && typeof promoCodes === 'object') {
    const promoCodesArray = Object.values(promoCodes);
    const activePromos = promoCodesArray.filter(promo => promo.active && promo.balance > 0);
    promoCount = activePromos.length;
    // Show dollar amount only when there's exactly one DOLLAR-type promo
    if (promoCount === 1 && activePromos[0].promoType === 'DOLLAR') {
      promoCodeBalance = activePromos[0].balance;
    }
  }

  const unclaimedGiftCount = giftClaims.filter(claim => !isGiftClaimClaimed(claim)).length;

  return (
    <div className="flex items-center gap-2">

      {/* Balance pill */}
      {promoCount > 0 && (
        <div className="hidden sm:flex items-center gap-2">
          <button
            type="button"
            onClick={onShowPromos}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-opacity hover:opacity-80 focus:outline-none"
            style={{
              backgroundColor: `color-mix(in srgb, ${navTextColor} 12%, transparent)`,
              color: navTextColor,
              border: `1px solid color-mix(in srgb, ${navTextColor} 25%, transparent)`,
            }}
            aria-label={promoCodeBalance !== null ? `Available promo balance: $${promoCodeBalance.toFixed(2)}` : `${promoCount} active promos`}
          >
            <Wallet className="w-3.5 h-3.5" />
            {promoCodeBalance !== null
              ? <span>${promoCodeBalance.toFixed(2)}</span>
              : <span>{promoCount} promo{promoCount !== 1 ? 's' : ''}</span>
            }
          </button>

          {/* Divider */}
          <div className="w-px h-5 bg-white/20 mx-1" />
        </div>
      )}

      {/* Primary group: Search + Cart + User */}
      <div className="flex items-center gap-1">

        {/* Search icon — mobile only */}
        <button
          type="button"
          onClick={isSearchOpen && searchLoading ? undefined : closingSpinner ? undefined : isSearchOpen ? handleClearAndClose : onMobileSearchOpen}
          className={`sm:hidden p-2.5 rounded-md transition-colors focus:outline-none ${(isSearchOpen && searchLoading) || closingSpinner ? 'cursor-default' : 'hover:bg-white/10'}`}
          style={{ color: themeColors?.header_cart_background || '#9ca3af' }}
          aria-label={isSearchOpen ? 'Close search' : 'Open search'}
        >
          {isSearchOpen
            ? (searchLoading && !isUserClosingRef.current)
              ? <Loader2 className="w-6 h-6 animate-spin" aria-hidden="true" />
              : <X className="w-6 h-6" aria-hidden="true" />
            : closingSpinner
              ? <Loader2 className="w-6 h-6 animate-spin" aria-hidden="true" />
              : <Search className="w-6 h-6" aria-hidden="true" />
          }
        </button>

        {/* Search — desktop only */}
        <div className="hidden sm:flex items-center">
          {isSearchOpen ? (
            <div className={`flex items-center gap-1 ${isClosingSearch ? 'animate-search-collapse' : 'animate-search-expand'}`}>
              <div className="relative flex items-center">
                <Search className="absolute left-2.5 w-4 h-4 pointer-events-none" style={{ color: themeColors?.header_cart_background || '#9ca3af' }} aria-hidden="true" />
                <input
                  ref={searchInputRef}
                  type="search"
                  value={searchQuery}
                  onChange={onSearchChange}
                  onKeyDown={(e) => e.key === 'Escape' && handleClose()}
                  placeholder="Search products..."
                  className="w-64 lg:w-80 pl-9 pr-3 py-1.5 rounded-lg text-sm focus:outline-none [&::-webkit-search-cancel-button]:hidden [&::placeholder]:opacity-50"
                  style={{
                    color: navTextColor,
                    backgroundColor: `color-mix(in srgb, ${navTextColor} 10%, transparent)`,
                    border: `1px solid color-mix(in srgb, ${navTextColor} 30%, transparent)`,
                  }}
                  onFocus={e => e.currentTarget.style.border = `1px solid color-mix(in srgb, ${navTextColor} 60%, transparent)`}
                  onBlur={e => e.currentTarget.style.border = `1px solid color-mix(in srgb, ${navTextColor} 30%, transparent)`}
                  aria-label="Search products"
                />
              </div>
              <button
                type="button"
                onClick={searchLoading ? undefined : handleClearAndClose}
                className={`p-2 rounded-md transition-colors focus:outline-none ${searchLoading ? 'cursor-default' : 'hover:bg-white/10'}`}
                style={{ color: themeColors?.header_cart_background || '#9ca3af' }}
                aria-label="Close search"
              >
                {searchLoading && !isUserClosingRef.current
                  ? <Loader2 className="w-6 h-6 animate-spin" aria-hidden="true" />
                  : <X className="w-6 h-6" aria-hidden="true" />
                }
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={closingSpinner ? undefined : onSearchOpen}
              className={`p-2.5 rounded-md transition-colors focus:outline-none ${closingSpinner ? 'cursor-default' : 'hover:bg-white/10'}`}
              style={{ color: themeColors?.header_cart_background || '#9ca3af' }}
              aria-label="Open search"
            >
              {closingSpinner
                ? <Loader2 className="w-6 h-6 animate-spin" aria-hidden="true" />
                : <Search className="w-6 h-6" aria-hidden="true" />
              }
            </button>
          )}
        </div>

        {/* Cart */}
        {showCart && (
          <div
            className="relative"
            onMouseEnter={onCartMouseEnter}
            onMouseLeave={onCartMouseLeave}
          >
            <button
              type="button"
              onClick={onCartClick}
              className="p-2.5 rounded-md transition-colors focus:outline-none hover:bg-white/10"
              style={{ color: themeColors?.header_cart_background || '#9ca3af' }}
              aria-label={`Shopping cart${cartItemsCount > 0 ? ` with ${cartItemsCount} items` : ', empty'}`}
            >
              <ShoppingCart className="w-6 h-6" aria-hidden="true" />
            </button>
            {cartItemsCount > 0 && (
              <span
                className="absolute -top-1 -right-1 font-bold min-w-[20px] h-[20px] rounded-full flex items-center justify-center px-1.5 pointer-events-none"
                style={{
                  backgroundColor: cartBadgeBackground,
                  color: cartBadgeText,
                  fontSize: '10px',
                  lineHeight: 1,
                }}
                aria-hidden="true"
              >
                {cartItemsCount > 999 ? '999+' : cartItemsCount}
              </span>
            )}
          </div>
        )}

        {/* Gift claims */}
        {unclaimedGiftCount > 0 && (
          <div className="relative">
            <button
              type="button"
              onClick={onShowGiftClaims}
              className="p-2.5 rounded-md transition-colors focus:outline-none hover:bg-white/10"
              style={{ color: themeColors?.header_cart_background || '#9ca3af' }}
              aria-label={`${unclaimedGiftCount} active gift${unclaimedGiftCount !== 1 ? 's' : ''} available to claim`}
            >
              <Gift className="w-6 h-6 animate-bell-shake" aria-hidden="true" />
            </button>
            <span
              className="absolute -top-1 -right-1 font-bold min-w-[20px] h-[20px] rounded-full flex items-center justify-center px-1.5 pointer-events-none"
              style={{
                backgroundColor: cartBadgeBackground,
                color: cartBadgeText,
                fontSize: '10px',
                lineHeight: 1,
              }}
              aria-hidden="true"
            >
              {unclaimedGiftCount > 999 ? '999+' : unclaimedGiftCount}
            </span>
          </div>
        )}

        {/* User Menu */}
        {isAuthenticated && (
          <UserMenu onShowPromos={onShowPromos} onShowGiftClaims={onShowGiftClaims} themeColors={themeColors} />
        )}

        {/* Login (guests) */}
        {isGuest && (
          <a
            href="/login"
            className="flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors focus:outline-none hover:opacity-90"
            style={{
              backgroundColor: themeColors?.header_cart_background || '#6D6A69',
              color: themeColors?.header_cart_text_color || '#ffffff',
            }}
          >
            <LogIn className="w-4 h-4" aria-hidden="true" />
            <span>Login</span>
          </a>
        )}


      </div>
    </div>
  );
}
